import { useLayoutEffect, useMemo, useState } from 'react';
const formatValueToCss = (value) => {
    if (value === undefined)
        return '';
    if (typeof value === 'number')
        return `${value}px`;
    return value;
};
const formatTrackSize = (width, defaultWidth) => {
    if (!width) {
        return `minmax(${defaultWidth}px, 1fr)`;
    }
    if ('fixed' in width) {
        return width.fixed === 'min-content' ? 'min-content' : formatValueToCss(width.fixed);
    }
    const min = width.min === 'min-content' ? 'min-content' : formatValueToCss(width.min);
    const max = formatValueToCss(width.max);
    return `minmax(${min}, ${max})`;
};
const getStickyColumnWidth = (width, defaultWidth) => {
    if (!width)
        return defaultWidth;
    if ('fixed' in width && typeof width.fixed === 'number')
        return width.fixed;
    if ('min' in width && typeof width.min === 'number')
        return width.min;
    if ('max' in width && typeof width.max === 'number')
        return width.max;
    return defaultWidth;
};
const validateStickyColumn = (width) => {
    if (!width)
        return;
    if ('fixed' in width && width.fixed === 'min-content') {
        throw new Error('min-content width is not supported for sticky columns. Sticky columns require fixed pixel widths for proper positioning.');
    }
    if ('min' in width && width.min === 'min-content') {
        throw new Error('min-content width is not supported for sticky columns. Sticky columns require fixed pixel widths for proper positioning.');
    }
    if ('max' in width && width.max === 'max-content') {
        throw new Error('max-content width is not supported for sticky columns. Sticky columns require fixed pixel widths for proper positioning.');
    }
};
const getColumnLayout = (columns, { headerAction, rowAction, rowActionWidth, }) => {
    const defaultWidth = 100;
    const layout = {
        columnsStyle: [],
        stickyLeftPositions: {},
        stickyRightPositions: {},
        stickyLeftWidth: 0,
        stickyRightWidth: 0,
    };
    columns.forEach((column, index) => {
        const meta = column.columnDef.meta ?? {};
        const width = meta.width;
        if (meta.sticky?.enabled) {
            validateStickyColumn(width);
            const stickyWidth = getStickyColumnWidth(width, defaultWidth);
            layout.columnsStyle.push(`${stickyWidth}px`);
            if (meta.sticky.side !== 'right') {
                layout.stickyLeftPositions[index] = layout.stickyLeftWidth;
                layout.stickyLeftWidth += stickyWidth;
            }
        }
        else {
            layout.columnsStyle.push(formatTrackSize(width, defaultWidth));
        }
    });
    if (headerAction || rowAction) {
        const actionWidth = rowActionWidth > 0 ? rowActionWidth : 0;
        layout.columnsStyle.push(actionWidth > 0 ? `${actionWidth}px` : 'max-content');
        layout.stickyRightWidth += actionWidth;
    }
    columns.toReversed().forEach((column, index) => {
        const meta = column.columnDef.meta ?? {};
        const width = meta.width;
        const columnIndex = columns.length - index - 1;
        if (meta.sticky?.enabled && meta.sticky.side === 'right') {
            const stickyWidth = getStickyColumnWidth(width, defaultWidth);
            layout.stickyRightPositions[columnIndex] = layout.stickyRightWidth;
            layout.stickyRightWidth += stickyWidth;
        }
    });
    return {
        ...layout,
        columnsStyle: layout.columnsStyle.join(' '),
    };
};
export const useColumnLayout = (columns, { isLoading, tableRef, headerAction, rowAction, }) => {
    const [rowActionWidth, setRowActionWidth] = useState(0);
    const layout = useMemo(() => getColumnLayout(columns, { headerAction, rowAction, rowActionWidth }), [columns, headerAction, rowAction, rowActionWidth]);
    useLayoutEffect(() => {
        if (isLoading) {
            return;
        }
        if (!tableRef.current) {
            return;
        }
        if (!rowAction && !headerAction) {
            if (rowActionWidth !== 0) {
                setRowActionWidth(0);
            }
            return;
        }
        const actionCells = Array.from(tableRef.current.querySelectorAll('[data-role="body-action-cell"]'));
        const actionCellWidths = actionCells.map((cell) => cell.getBoundingClientRect().width);
        const headerCell = tableRef.current.querySelector('[data-role="header-action-cell"]');
        const headerWidth = headerCell ? headerCell.getBoundingClientRect().width : 0;
        const maxWidth = Math.max(0, ...actionCellWidths, headerWidth);
        if (Math.ceil(maxWidth) !== rowActionWidth) {
            setRowActionWidth(Math.ceil(maxWidth));
        }
    }, [isLoading, rowAction, headerAction, rowActionWidth, tableRef]);
    return layout;
};
//# sourceMappingURL=useColumnLayout.js.map